/**
 * ScriptVoiceCard - Card 2: Configure Script & Voice Settings (INPUT)
 *
 * Consistent typography with framework:
 * - text-xs for labels, text-[11px] for secondary
 * - Voice + Quality on same row
 * - Collapsible script and voice prompts
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { VoiceSelector } from '../VoiceSelector';
import { QualityToggle } from './QualityToggle';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  FileText,
  Settings2,
  ChevronDown,
  Loader2,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import type { TTSVoice } from '@/services/audioService';

interface ScriptVoiceCardProps {
  defaultExpanded: boolean;
  script: string;
  wordCount: number;
  onScriptChange: (script: string) => void;
  hasUnsavedChanges: boolean;
  voices: TTSVoice[];
  selectedVoice: string;
  onVoiceChange: (voiceId: string) => void;
  selectedModel: 'flash' | 'pro';
  onModelChange: (model: 'flash' | 'pro') => void;
  promptValue: string;
  onPromptChange: (prompt: string) => void;
  isVideoLevelPrompts?: boolean;
  hasAudio: boolean;
  /** When undefined, the Generate/Regenerate button is hidden (used to gate beta-only controls). */
  onGenerate?: () => void;
  isGenerating: boolean;
  isLoading?: boolean;
  className?: string;
}

export const ScriptVoiceCard: React.FC<ScriptVoiceCardProps> = ({
  defaultExpanded,
  script,
  wordCount,
  onScriptChange,
  hasUnsavedChanges,
  voices,
  selectedVoice,
  onVoiceChange,
  selectedModel,
  onModelChange,
  promptValue,
  onPromptChange,
  isVideoLevelPrompts = false,
  hasAudio,
  onGenerate,
  isGenerating,
  isLoading = false,
  className,
}) => {
  const [isScriptExpanded, setIsScriptExpanded] = React.useState(defaultExpanded);
  const [isPromptsExpanded, setIsPromptsExpanded] = React.useState(false);

  // Update expansion when defaultExpanded changes (no audio = expanded)
  React.useEffect(() => {
    if (!hasAudio) {
      setIsScriptExpanded(true);
    }
  }, [hasAudio]);

  // Truncate script for preview
  const scriptPreview = React.useMemo(() => {
    if (!script) return 'No script yet...';
    const maxLength = 50;
    if (script.length <= maxLength) return script;
    return script.slice(0, maxLength) + '...';
  }, [script]);

  return (
    <div className={cn('rounded-lg border border-border/50 bg-card overflow-hidden', className)}>
      {/* Script Section */}
      <Collapsible open={isScriptExpanded} onOpenChange={setIsScriptExpanded}>
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Script</span>
              {hasUnsavedChanges && (
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isScriptExpanded && (
                <span className="text-[11px] text-muted-foreground truncate max-w-[120px]">
                  {scriptPreview}
                </span>
              )}
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 tabular-nums">
                {wordCount} words
              </Badge>
              <ChevronDown className={cn(
                'w-4 h-4 text-muted-foreground transition-transform',
                isScriptExpanded && 'rotate-180'
              )} />
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3">
            <Textarea
              value={script}
              onChange={(e) => onScriptChange(e.target.value)}
              placeholder="Write your narration for this slide..."
              className="min-h-[100px] text-sm resize-none border-border/50 bg-background/50 focus:bg-background"
              disabled={isLoading || isGenerating}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Voice + Quality Row */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border/30 bg-muted/10">
        {/* Voice Selector */}
        <div className="flex-1 min-w-0">
          <VoiceSelector
            voices={voices}
            selectedVoice={selectedVoice}
            onVoiceChange={onVoiceChange}
            disabled={isLoading || isGenerating}
            className="w-full"
          />
        </div>

        {/* Quality Toggle */}
        <QualityToggle
          value={selectedModel}
          onChange={onModelChange}
          disabled={isLoading || isGenerating}
        />

        {/* Voice Prompts Toggle */}
        <button
          onClick={() => setIsPromptsExpanded(!isPromptsExpanded)}
          className={cn(
            'w-8 h-8 flex items-center justify-center rounded-md hover:bg-muted/50 transition-colors',
            isPromptsExpanded && 'bg-muted/50',
            (isVideoLevelPrompts && promptValue) && 'text-primary'
          )}
          title="Voice style settings"
        >
          <Settings2 className="h-4 w-4" />
        </button>
      </div>

      {/* Voice Prompts - Collapsible */}
      {isPromptsExpanded && (
        <div className="px-3 pb-3 border-t border-border/30">
          <div className="pt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Voice Style</span>
              {isVideoLevelPrompts && promptValue && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-primary/50 text-primary dark:text-accent-foreground">
                  Video-wide
                </Badge>
              )}
            </div>
            <Textarea
              value={promptValue}
              onChange={(e) => onPromptChange(e.target.value)}
              placeholder="e.g., Speak with enthusiasm, pause between sentences"
              className={cn(
                'min-h-[60px] text-sm resize-none border-border/50 bg-background/50',
                isVideoLevelPrompts && 'bg-primary/5'
              )}
              maxLength={500}
              readOnly={isVideoLevelPrompts}
              disabled={isLoading || isGenerating}
            />
            <p className="text-[11px] text-muted-foreground">
              {isVideoLevelPrompts ? 'This prompt is set at the video level.' : 'Natural language instructions for the AI voice.'}
            </p>
          </div>
        </div>
      )}

      {/* Generate Button */}
      {onGenerate && (
        <div className="px-3 py-2.5 border-t border-border/30 bg-muted/5">
          {!hasAudio ? (
            <Button
              variant="default"
              size="sm"
              onClick={onGenerate}
              disabled={!script.trim() || isGenerating || isLoading}
              className="w-full h-9"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Narration
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onGenerate}
              disabled={!script.trim() || isGenerating || isLoading}
              className="w-full h-8"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ScriptVoiceCard;
